import React, { useState, useEffect, useMemo, useRef } from 'react'
import {
  TrendingUp, TrendingDown, DollarSign, AlertTriangle,
  BarChart3, Shield, Loader2, ChevronDown, ChevronUp,
  Download, FileText, Plus, Info, ArrowRight, Target, Clock, Zap, RefreshCw, Pencil
} from 'lucide-react'
import { toast } from 'sonner'

const API = process.env.REACT_APP_BACKEND_URL

const fmt = (v) => {
  if (v == null) return '\u2014'
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v)
}

const pct = (v) => v != null ? `${v.toFixed(1)}%` : '\u2014'

const spColor = (v) => v > 70 ? 'text-red-600' : v > 50 ? 'text-amber-600' : v > 30 ? 'text-blue-600' : 'text-emerald-600'
const spBgClass = (v) => v > 70 ? 'bg-red-50 border-red-200' : v > 50 ? 'bg-amber-50 border-amber-200' : v > 30 ? 'bg-blue-50 border-blue-200' : 'bg-emerald-50 border-emerald-200'
const spBarColor = (v) => v > 70 ? 'bg-red-500' : v > 50 ? 'bg-amber-500' : v > 30 ? 'bg-blue-500' : 'bg-emerald-500'

function EditableLieu({ sinistre, onRefresh }) {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(sinistre.lieu || '')
  const origRef = useRef(sinistre.lieu || '')

  async function handleSave() {
    if (val === origRef.current) { setEditing(false); return }
    try {
      const r = await fetch(`${API}/api/sinistres/${sinistre.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lieu: val }),
      })
      if (r.ok) {
        toast.success('Lieu mis a jour')
        origRef.current = val
        setEditing(false)
        onRefresh?.()
      }
    } catch { toast.error('Erreur') }
  }

  if (editing) {
    return (
      <input
        type="text" value={val} onChange={e => setVal(e.target.value)}
        className="w-full px-2 py-1 text-xs border border-indigo-300 rounded focus:ring-1 focus:ring-indigo-400"
        autoFocus onBlur={handleSave}
        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setVal(origRef.current); setEditing(false) } }}
        data-testid={`edit-lieu-${sinistre.id}`}
      />
    )
  }

  return (
    <span className="cursor-pointer group flex items-center gap-1" onClick={() => setEditing(true)}>
      {sinistre.lieu || <span className="text-slate-300 italic">Saisir le lieu</span>}
      <Pencil size={10} className="text-slate-300 opacity-0 group-hover:opacity-100" />
    </span>
  )
}

const SOURCE_LABELS = {
  provision_compagnie: { label: 'Charge assureur = provision compagnie − franchises à charge client', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
  aucun: { label: 'Aucune donnee', color: 'text-slate-500 bg-slate-50 border-slate-200' },
}

export default function ClientBudgetTab({ tenantId, policeId }) {
  const [data, setData] = useState(null)
  const [spData, setSpData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [exerciceIdx, setExerciceIdx] = useState(0)
  const [expandedPolice, setExpandedPolice] = useState(null)
  const [showAddPrime, setShowAddPrime] = useState(false)
  const [polices, setPolices] = useState([])
  const [activeTab, setActiveTab] = useState('sp') // 'sp' | 'primes' | 'sinistres'
  const [recalculating, setRecalculating] = useState(false)

  useEffect(() => {
    fetchAll()
  }, [tenantId, exerciceIdx])

  async function handleRecalculerTout() {
    setRecalculating(true)
    try {
      await fetch(`${API}/api/budget/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenant_id: tenantId }),
      })
      await fetchAll()
      toast.success('Budget recalculé')
    } catch (e) {
      toast.error('Erreur recalcul')
    } finally {
      setRecalculating(false)
    }
  }

  async function fetchAll() {
    setLoading(true)
    try {
      const policeParam = policeId ? `&police_id=${policeId}` : ''
      const [budgetRes, spRes, polRes] = await Promise.all([
        fetch(`${API}/api/budget/tenant/${tenantId}?exercice_idx=${exerciceIdx}${policeParam}`),
        fetch(`${API}/api/budget/sp-complet/${tenantId}?exercice_idx=${exerciceIdx}${policeParam}`),
        fetch(`${API}/api/polices?tenant_id=${tenantId}`),
      ])
      const [budgetJson, spJson, polJson] = await Promise.all([
        budgetRes.json(), spRes.json(), polRes.json(),
      ])
      if (budgetJson.success) setData(budgetJson)
      if (spJson.sp_reel !== undefined) setSpData(spJson)
      setPolices(polJson.polices || [])
    } catch (e) {
      console.error('Budget fetch error:', e)
    } finally {
      setLoading(false)
    }
  }

  const chartData = useMemo(() => {
    if (!data?.primes) return []
    const byYear = {}
    data.primes.forEach(p => {
      const y = p.annee
      if (!byYear[y]) byYear[y] = { annee: y, prime_ht: 0, prime_ttc: 0 }
      byYear[y].prime_ht += p.prime_nette || 0
      byYear[y].prime_ttc += p.prime_totale || 0
    })
    return Object.values(byYear).sort((a, b) => a.annee - b.annee)
  }, [data])

  const maxPrime = useMemo(() => Math.max(...chartData.map(d => d.prime_ttc), 1), [chartData])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="budget-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    )
  }

  if (!data || !spData) {
    return (
      <div className="p-6 text-center text-sm text-slate-500" data-testid="budget-error">
        Impossible de charger les donnees budgetaires.
      </div>
    )
  }

  const r = data.resume
  const sp = spData
  const annee = data.annee || new Date().getFullYear()
  const srcInfo = SOURCE_LABELS.provision_compagnie
  const exercices = sp.exercices_disponibles || []
  const exerciceLabel = sp.exercice_label || ''

  return (
    <div className="p-6 space-y-6" data-testid="budget-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-slate-800" data-testid="budget-title">
            Pilotage Budget & S/P
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Exercice contractuel {'\u2022'} Calcul au {new Date().toLocaleDateString('fr-FR')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRecalculerTout}
            disabled={recalculating}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-700 text-white rounded-md hover:bg-slate-800 transition-colors disabled:opacity-50"
            data-testid="recalcul-global-btn"
          >
            <RefreshCw size={12} className={recalculating ? 'animate-spin' : ''} />
            {recalculating ? 'Recalcul...' : 'Recalculer'}
          </button>
          <button
            onClick={() => setShowAddPrime(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors"
            data-testid="add-prime-btn"
          >
            <Plus size={12} /> Saisir prime
          </button>
          <button
            onClick={() => window.open(`${API}/api/export/bilan-pdf/${tenantId}`, '_blank')}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
            data-testid="export-pdf-btn"
          >
            <Download size={12} /> PDF
          </button>
          <select
            value={exerciceIdx}
            onChange={e => setExerciceIdx(Number(e.target.value))}
            className="text-sm border border-slate-200 rounded-md px-3 py-1.5 bg-white text-slate-700"
            data-testid="budget-exercice-select"
          >
            {exercices.length > 0 ? exercices.map(ex => (
              <option key={ex.index} value={ex.index}>{ex.label}</option>
            )) : (
              <option value={0}>{exerciceLabel || 'Exercice en cours'}</option>
            )}
          </select>
        </div>
      </div>

      {/* Exercise period banner */}
      {exerciceLabel && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="exercice-banner">
          <Target size={14} className="text-indigo-500 shrink-0" />
          <span className="text-sm font-semibold text-indigo-700" data-testid="exercice-label">{exerciceLabel}</span>
          {exerciceIdx === 0 && (
            <span className="text-xs bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full font-medium ml-2">En cours</span>
          )}
        </div>
      )}

      {/* Source charge indicator */}
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs border ${srcInfo.color}`} data-testid="source-charge-banner">
        <Info size={13} />
        <span className="font-medium">{srcInfo.label}</span>
        {sp.nb_sans_provision > 0 && (
          <span className="flex items-center gap-1 ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">
            <AlertTriangle size={11} />
            {sp.nb_sans_provision} sinistre(s) sans provision
          </span>
        )}
        {sp.filtrage && sp.filtrage.exclus > 0 && (
          <span className="ml-auto text-slate-500">{sp.filtrage.exclus} sinistre(s) exclu(s) (amiable/retention)</span>
        )}
      </div>

      {/* S/P Contrat par contrat — Pas de KPI globaux additionnés */}
      {sp.sp_par_police?.length > 1 ? (
        /* Vue multi-contrats : afficher chaque contrat individuellement */
        <div className="space-y-4" data-testid="budget-contrats">
          {sp.sp_par_police.map((p, i) => (
            <div key={p.police_id || i} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`contrat-sp-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <span className="text-sm font-semibold text-slate-800">{p.numero_police}</span>
                  <span className="text-xs text-slate-400 ml-2">{p.libelle || p.type_police}</span>
                </div>
                <span className="text-xs text-slate-400">{p.exercice_label}</span>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
                <KPICard label="Prime HT" value={fmt(p.prime_ht)} icon={DollarSign} color="indigo" testId={`kpi-prime-${i}`} />
                <KPICard label="Charge assureur" value={fmt(p.charge_sinistres)} sub={chargeSub(p, p.nb_sinistres)} icon={TrendingDown} color="amber" testId={`kpi-charge-${i}`} />
                <KPICard label="Provisions" value={fmt(p.provisions || 0)} icon={Shield} color="blue" testId={`kpi-provisions-${i}`} />
                <SPKPICard label="S/P reel" value={p.sp_reel} formula="Charge / Prime HT" icon={BarChart3} testId={`kpi-sp-reel-${i}`} />
                <SPKPICard label="S/P a date" value={p.sp_a_date} formula="Charge / Prime proratisee" icon={Clock} testId={`kpi-sp-a-date-${i}`} />
                <SPKPICard label="S/P annualise" value={p.sp_annualise} formula="Projection / Prime HT" icon={Zap} testId={`kpi-sp-annualise-${i}`} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Vue mono-contrat : KPI classiques */
        <>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3" data-testid="budget-kpis">
            <KPICard label="Prime HT" value={fmt(sp.prime_ht)} icon={DollarSign} color="indigo" testId="kpi-prime-ht" />
            <KPICard
              label="Charge assureur"
              value={fmt(sp.charge_sinistres)}
              sub={chargeSub(sp, sp.nb_sinistres_assurance)}
              icon={TrendingDown}
              color="amber"
              testId="kpi-charge"
            />
            <KPICard label="Provisions" value={fmt(sp.provisions)} icon={Shield} color="blue" testId="kpi-provisions" />
            <SPKPICard label="S/P reel" value={sp.sp_reel} formula="Charge / Prime HT" icon={BarChart3} testId="kpi-sp-reel" />
            <SPKPICard label="S/P a date" value={sp.sp_a_date} formula="Charge / Prime proratisee (exercice)" icon={Clock} testId="kpi-sp-a-date" />
            <SPKPICard label="S/P annualise" value={sp.sp_annualise} formula="Projection exercice / Prime HT" icon={Zap} testId="kpi-sp-annualise" />
          </div>

          {sp.sp_par_police?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="proratisation-detail">
              <div className="flex items-center gap-6 text-xs text-slate-500">
                <span>
                  <Target size={12} className="inline mr-1 text-slate-400" />
                  Prime proratisee : <strong className="text-slate-700">{fmt(sp.prime_proratisee)}</strong>
                </span>
                <span>
                  Charge projetee ({sp.sp_par_police[0]?.jours_periode}j) : <strong className="text-slate-700">{fmt(sp.charge_projetee)}</strong>
                </span>
                <span>
                  Jours ecoules : <strong className="text-slate-700">{sp.sp_par_police[0]?.jours_ecoules}/{sp.sp_par_police[0]?.jours_periode}j</strong>
                </span>
              </div>
            </div>
          )}
        </>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1" data-testid="budget-tabs">
        {[
          { key: 'sp', label: 'S/P par police' },
          { key: 'primes', label: 'Detail primes' },
          { key: 'sinistres', label: 'Sinistres' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 px-4 py-2 text-xs font-medium rounded-md transition-colors ${
              activeTab === tab.key
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid={`tab-${tab.key}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content: S/P par police */}
      {activeTab === 'sp' && (
        <div className="bg-white border border-slate-200 rounded-lg" data-testid="sp-par-police-table">
          <div className="px-5 py-3 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-700">S/P par contrat / police</h3>
          </div>
          {(sp.sp_par_police || []).length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">Aucune police avec prime pour cet exercice</div>
          ) : (
            <table className="w-full text-sm" data-testid="sp-police-tbl">
              <thead>
                <tr className="text-xs text-slate-400 border-b border-slate-100">
                  <th className="px-5 py-2.5 text-left font-medium">Police</th>
                  <th className="px-3 py-2.5 text-right font-medium">Prime HT</th>
                  <th className="px-3 py-2.5 text-right font-medium">Sinistres</th>
                  <th className="px-3 py-2.5 text-right font-medium">Charge</th>
                  <th className="px-3 py-2.5 text-right font-medium">S/P reel</th>
                  <th className="px-3 py-2.5 text-right font-medium">S/P a date</th>
                  <th className="px-3 py-2.5 text-right font-medium">S/P annualise</th>
                  <th className="px-3 py-2.5 text-center font-medium">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {sp.sp_par_police.map((p, i) => (
                  <tr key={p.police_id || i} className="hover:bg-slate-50" data-testid={`sp-row-${i}`}>
                    <td className="px-5 py-3">
                      <div className="font-medium text-slate-700">{p.numero_police}</div>
                      <div className="text-xs text-slate-400">{p.libelle || p.type_police}</div>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-700">{fmt(p.prime_ht)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-600">{p.nb_sinistres}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-700">{fmt(p.charge_sinistres)}</td>
                    <td className="px-3 py-3 text-right">
                      <span className={`font-semibold tabular-nums ${spColor(p.sp_reel)}`}>{pct(p.sp_reel)}</span>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <span className={`font-medium tabular-nums ${spColor(p.sp_a_date)}`}>{pct(p.sp_a_date)}</span>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <span className={`font-medium tabular-nums ${spColor(p.sp_annualise)}`}>{pct(p.sp_annualise)}</span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <SourceBadge nbSansProv={p.nb_sans_provision || 0} />
                    </td>
                  </tr>
                ))}
              </tbody>
              {sp.sp_par_police.length > 1 && (
                <tfoot>
                  <tr className="border-t-2 border-slate-200 bg-slate-50">
                    <td className="px-5 py-2.5 text-xs font-bold text-slate-700">TOTAL CLIENT</td>
                    <td className="px-3 py-2.5 text-right text-xs font-bold tabular-nums text-slate-700">{fmt(sp.prime_ht)}</td>
                    <td className="px-3 py-2.5 text-right text-xs font-bold tabular-nums">{sp.nb_sinistres_assurance}</td>
                    <td className="px-3 py-2.5 text-right text-xs font-bold tabular-nums text-slate-700">{fmt(sp.charge_sinistres)}</td>
                    <td className="px-3 py-2.5 text-right">
                      <span className={`text-xs font-bold tabular-nums ${spColor(sp.sp_reel)}`}>{pct(sp.sp_reel)}</span>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <span className={`text-xs font-bold tabular-nums ${spColor(sp.sp_a_date)}`}>{pct(sp.sp_a_date)}</span>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <span className={`text-xs font-bold tabular-nums ${spColor(sp.sp_annualise)}`}>{pct(sp.sp_annualise)}</span>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <SourceBadge nbSansProv={sp.nb_sans_provision || 0} />
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>
      )}

      {/* Tab Content: Primes detail */}
      {activeTab === 'primes' && (
        <>
          {/* Evolution Chart */}
          {chartData.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="budget-evolution-chart">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Evolution des primes</h3>
              <div className="flex items-end gap-3 h-48">
                {chartData.map(d => {
                  const heightPct = (d.prime_ttc / maxPrime) * 100
                  const htPct = (d.prime_ht / maxPrime) * 100
                  const isCurrentYear = d.annee === annee
                  return (
                    <div key={d.annee} className="flex-1 flex flex-col items-center gap-1" data-testid={`chart-bar-${d.annee}`}>
                      <span className="text-[10px] text-slate-500 tabular-nums">{fmt(d.prime_ht)}</span>
                      <div className="w-full flex flex-col items-center" style={{ height: '140px' }}>
                        <div className="w-full flex items-end justify-center h-full gap-1">
                          <div
                            className={`w-5 rounded-t transition-all ${isCurrentYear ? 'bg-indigo-500' : 'bg-indigo-200'}`}
                            style={{ height: `${htPct}%`, minHeight: d.prime_ht > 0 ? '4px' : '0' }}
                            title={`HT: ${fmt(d.prime_ht)}`}
                          />
                          <div
                            className={`w-5 rounded-t transition-all ${isCurrentYear ? 'bg-slate-400' : 'bg-slate-200'}`}
                            style={{ height: `${heightPct}%`, minHeight: d.prime_ttc > 0 ? '4px' : '0' }}
                            title={`TTC: ${fmt(d.prime_ttc)}`}
                          />
                        </div>
                      </div>
                      <span className={`text-xs font-medium ${isCurrentYear ? 'text-indigo-600' : 'text-slate-500'}`}>
                        {d.annee}
                      </span>
                    </div>
                  )
                })}
              </div>
              <div className="flex items-center gap-4 mt-3 justify-center">
                <span className="flex items-center gap-1.5 text-[10px] text-slate-500">
                  <span className="w-3 h-2 rounded-sm bg-indigo-500 inline-block" /> Prime HT
                </span>
                <span className="flex items-center gap-1.5 text-[10px] text-slate-500">
                  <span className="w-3 h-2 rounded-sm bg-slate-400 inline-block" /> Prime TTC
                </span>
              </div>
            </div>
          )}

          {/* Primes detail by police */}
          <div className="bg-white border border-slate-200 rounded-lg" data-testid="budget-primes-detail">
            <div className="px-5 py-3 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-700">Detail des primes {exerciceLabel ? `(${exerciceLabel})` : ''}</h3>
            </div>
            {(data.primes_annee || []).length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-400">Aucune prime saisie pour cet exercice</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {data.primes_annee.map((p, i) => {
                  const police = p.polices || {}
                  const isExpanded = expandedPolice === p.id
                  return (
                    <div key={p.id || i} data-testid={`prime-row-${i}`}>
                      <div
                        className="px-5 py-3 flex items-center gap-3 cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => setExpandedPolice(isExpanded ? null : p.id)}
                      >
                        <Shield size={14} className="text-indigo-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-700 truncate">
                            {police.numero_police || 'Police'} {'\u2014'} {police.libelle || police.type_police || ''}
                          </p>
                          <p className="text-xs text-slate-400">{police.compagnies?.nom || ''}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-semibold text-slate-800 tabular-nums">{fmt(p.prime_nette)}</p>
                          <p className="text-[10px] text-slate-400">HT</p>
                        </div>
                        {isExpanded ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
                      </div>
                      {isExpanded && (
                        <div className="px-5 pb-3 pl-12 grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                          <div>
                            <p className="text-slate-400">Prime nette (HT)</p>
                            <p className="font-medium text-slate-700">{fmt(p.prime_nette)}</p>
                          </div>
                          <div>
                            <p className="text-slate-400">Taxes</p>
                            <p className="font-medium text-slate-700">{fmt(p.taxes)}</p>
                          </div>
                          <div>
                            <p className="text-slate-400">Prime totale (TTC)</p>
                            <p className="font-medium text-slate-700">{fmt(p.prime_totale)}</p>
                          </div>
                          <div>
                            <p className="text-slate-400">Source</p>
                            <p className="font-medium text-slate-700">{p.source || '\u2014'}</p>
                          </div>
                          {p.commentaire && (
                            <div className="col-span-2 lg:col-span-4">
                              <p className="text-slate-400">Commentaire</p>
                              <p className="text-slate-600">{p.commentaire}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* Tab Content: Sinistres */}
      {activeTab === 'sinistres' && (
        <div className="bg-white border border-slate-200 rounded-lg" data-testid="budget-sinistres-summary">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700">Sinistres {exerciceLabel ? `(${exerciceLabel})` : ''}</h3>
            {sp.filtrage && (
              <span className="text-[10px] text-slate-400">
                {sp.filtrage.inclus} inclus dans S/P {'\u2022'} {sp.filtrage.exclus} exclus (amiable/retention)
              </span>
            )}
          </div>
          {(data.sinistres || []).length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">Aucun sinistre pour cet exercice</div>
          ) : (() => {
            const policeInfo = sp.sp_par_police?.[0] || {}
            const isFlotte = (policeInfo.type_police || '') === 'flotte'
            const sortedSinistres = [...data.sinistres].sort((a, b) => (b.date_fait || '').localeCompare(a.date_fait || ''))
            return (
            <table className="w-full text-sm" data-testid="budget-sinistres-table">
              <thead>
                <tr className="text-xs text-slate-400 border-b border-slate-100">
                  <th className="px-5 py-2 text-left font-medium">{isFlotte ? 'N client' : 'Lieu'}</th>
                  <th className="px-3 py-2 text-left font-medium">Date</th>
                  <th className="px-3 py-2 text-left font-medium">Nature</th>
                  <th className="px-3 py-2 text-left font-medium">Statut</th>
                  <th className="px-3 py-2 text-left font-medium">Mode</th>
                  <th className="px-3 py-2 text-right font-medium">Provision Cie</th>
                  <th className="px-3 py-2 text-right font-medium">Rembourse</th>
                  <th className="px-3 py-2 text-center font-medium">Inclus S/P</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {sortedSinistres.map((s, i) => {
                  const mg = (s.mode_gestion || '').toLowerCase()
                  const isExclu = mg === 'amiable' || mg === 'retention' || (s.est_amiable === true && !mg)
                  const dateFr = s.date_fait ? new Date(s.date_fait).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '\u2014'
                  return (
                    <tr key={s.id || i} className={`hover:bg-slate-50 ${isExclu ? 'opacity-50' : ''}`} data-testid={`sinistre-row-${i}`}>
                      <td className="px-5 py-2.5 text-slate-700 font-medium">
                        {isFlotte ? (s.numero_sinistre_client || s.numero_sinistre) : (
                          <EditableLieu sinistre={s} onRefresh={fetchAll} />
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600 text-xs tabular-nums">{dateFr}</td>
                      <td className="px-3 py-2.5 text-slate-600 capitalize">{s.nature_sinistre}</td>
                      <td className="px-3 py-2.5">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          s.statut === 'cloture' ? 'bg-emerald-100 text-emerald-700' :
                          s.statut === 'en_cours' ? 'bg-blue-100 text-blue-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {s.statut}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-xs capitalize text-slate-500">{s.mode_gestion || '\u2014'}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{fmt(s.provision_compagnie)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums font-medium text-slate-800">{fmt(s.montant_rembourse)}</td>
                      <td className="px-3 py-2.5 text-center">
                        {isExclu ? (
                          <span className="text-xs text-red-400 font-medium">Exclu</span>
                        ) : (
                          <span className="text-xs text-emerald-500 font-medium">Oui</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-200 bg-slate-50">
                  <td colSpan={5} className="px-5 py-2 text-xs font-semibold text-slate-600">Total (inclus S/P)</td>
                  <td className="px-3 py-2 text-right text-xs font-semibold tabular-nums text-slate-700">{fmt(sp.charge_sinistres)}</td>
                  <td className="px-3 py-2 text-right text-xs font-semibold tabular-nums text-slate-800">{fmt(sp.charge_sinistres)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
            )
          })()}
        </div>
      )}

      {/* Formulas transparency block */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="formulas-block">
        <h4 className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
          <Info size={12} />
          Formules de calcul (transparence DAF)
        </h4>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 text-[11px] text-slate-500">
          <div>
            <p className="font-medium text-slate-600">Charge sinistre</p>
            <p>provision_compagnie uniquement (pas de fallback)</p>
          </div>
          <div>
            <p className="font-medium text-slate-600">S/P reel</p>
            <p>charge_sinistres / prime_ht x 100</p>
          </div>
          <div>
            <p className="font-medium text-slate-600">S/P a date</p>
            <p>charge / prime_proratisee (jours ecoules dans exercice contractuel)</p>
          </div>
          <div>
            <p className="font-medium text-slate-600">S/P annualise</p>
            <p>(charge / jours_ecoules x duree_exercice) / prime_ht x 100</p>
          </div>
          <div>
            <p className="font-medium text-slate-600">Filtrage</p>
            <p>Exclu : mode_gestion = amiable | retention</p>
          </div>
          <div>
            <p className="font-medium text-slate-600">Periode</p>
            <p>Exercice contractuel (date_effet {'\u2192'} date_echeance), PAS annee civile</p>
          </div>
        </div>
      </div>

      {/* Add Prime Modal */}
      {showAddPrime && (
        <AddPrimeModal
          polices={polices}
          annee={annee}
          tenantId={tenantId}
          onClose={() => setShowAddPrime(false)}
          onSaved={() => { setShowAddPrime(false); fetchAll() }}
        />
      )}
    </div>
  )
}

/* ===== S/P KPI Card ===== */
function SPKPICard({ label, value, formula, icon: Icon, testId }) {
  const color = spColor(value)
  const bg = spBgClass(value)
  const bar = spBarColor(value)
  return (
    <div className={`border rounded-lg p-4 ${bg}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-slate-500 font-medium">{label}</span>
        <Icon size={14} className={color} />
      </div>
      <p className={`text-2xl font-bold ${color}`}>{pct(value)}</p>
      {/* visual bar */}
      <div className="w-full bg-white/50 rounded-full h-1.5 mt-2">
        <div className={`h-1.5 rounded-full ${bar} transition-all`} style={{ width: `${Math.min(value, 100)}%` }} />
      </div>
      <p className="text-[10px] text-slate-400 mt-1">{formula}</p>
    </div>
  )
}

/* ===== Source Badge ===== */
function SourceBadge({ nbSansProv }) {
  if (nbSansProv > 0) {
    return <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">{nbSansProv} sans prov.</span>
  }
  return <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-700">OK</span>
}

/* ===== Add Prime Modal ===== */
function AddPrimeModal({ polices, annee, tenantId, onClose, onSaved, editPrime }) {
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    police_id: editPrime?.police_id || (polices.length === 1 ? polices[0].id : ''),
    annee: editPrime?.annee || annee,
    prime_nette: editPrime?.prime_nette || '',
    taxes: editPrime?.taxes || '',
    prime_totale: editPrime?.prime_totale || '',
    source: editPrime?.source || 'saisie_manuelle',
    commentaire: editPrime?.commentaire || '',
  })

  useEffect(() => {
    const ht = parseFloat(formData.prime_nette) || 0
    const tax = parseFloat(formData.taxes) || 0
    if (ht > 0) {
      setFormData(prev => ({ ...prev, prime_totale: (ht + tax).toFixed(2) }))
    }
  }, [formData.prime_nette, formData.taxes])

  async function handleSubmit(e) {
    e.preventDefault()
    if (!formData.police_id || !formData.prime_nette) return
    setSaving(true)
    try {
      const res = await fetch(`${API}/api/budget/primes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          police_id: formData.police_id,
          annee: parseInt(formData.annee),
          prime_nette: parseFloat(formData.prime_nette),
          taxes: parseFloat(formData.taxes) || 0,
          prime_totale: parseFloat(formData.prime_totale) || parseFloat(formData.prime_nette),
          source: formData.source,
          commentaire: formData.commentaire,
        }),
      })
      if (res.ok) onSaved()
    } catch (e) {
      console.error(e)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="add-prime-modal">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="text-base font-semibold text-slate-800">{editPrime ? 'Modifier la prime' : 'Ajouter une prime'}</h3>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Police *</label>
            <select value={formData.police_id} onChange={e => setFormData({...formData, police_id: e.target.value})} required className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="prime-police-select">
              <option value="">Selectionner une police...</option>
              {polices.map(p => (
                <option key={p.id} value={p.id}>{p.numero_police} {'\u2014'} {p.libelle || p.type_police}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Annee *</label>
              <input type="number" value={formData.annee} onChange={e => setFormData({...formData, annee: e.target.value})} required className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="prime-annee" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Source</label>
              <select value={formData.source} onChange={e => setFormData({...formData, source: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                <option value="saisie_manuelle">Saisie manuelle</option>
                <option value="import">Import</option>
                <option value="avenant">Avenant</option>
                <option value="renouvellement">Renouvellement</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Prime HT *</label>
              <input type="number" step="0.01" value={formData.prime_nette} onChange={e => setFormData({...formData, prime_nette: e.target.value})} required className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="0.00" data-testid="prime-ht" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Taxes</label>
              <input type="number" step="0.01" value={formData.taxes} onChange={e => setFormData({...formData, taxes: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="0.00" data-testid="prime-taxes" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Prime TTC</label>
              <input type="number" step="0.01" value={formData.prime_totale} onChange={e => setFormData({...formData, prime_totale: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50" data-testid="prime-ttc" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Commentaire</label>
            <input type="text" value={formData.commentaire} onChange={e => setFormData({...formData, commentaire: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="Ex: Avenant, renouvellement..." />
          </div>
          <div className="flex justify-end gap-3 pt-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Annuler</button>
            <button type="submit" disabled={saving || !formData.police_id || !formData.prime_nette} className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50" data-testid="prime-submit">
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Decomposition : charge brute (provisions) - franchises a charge client - recours assureur = charge assureur
function chargeSub(d, nb) {
  const parts = [`${nb ?? 0} sinistre(s)`]
  const fr = Number(d.franchises_recuperees || 0)
  const rec = Number(d.recours_assureur || 0)
  if (fr > 0 || rec > 0) {
    let txt = `Brute ${fmt(d.charge_brute)}`
    if (fr > 0) txt += ` − franchises client ${fmt(fr)}`
    if (rec > 0) txt += ` − recours ${fmt(rec)}`
    parts.push(txt)
  }
  return parts.join(' · ')
}

function KPICard({ label, value, sub, icon: Icon, color, testId }) {
  const colors = {
    indigo: { bg: 'bg-indigo-50', icon: 'text-indigo-500', border: 'border-indigo-100' },
    slate:  { bg: 'bg-slate-50', icon: 'text-slate-500', border: 'border-slate-200' },
    amber:  { bg: 'bg-amber-50', icon: 'text-amber-500', border: 'border-amber-100' },
    blue:   { bg: 'bg-blue-50', icon: 'text-blue-500', border: 'border-blue-100' },
  }
  const c = colors[color] || colors.slate
  return (
    <div className={`${c.bg} border ${c.border} rounded-lg p-4`} data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-slate-500 font-medium">{label}</span>
        <Icon size={14} className={c.icon} />
      </div>
      <p className="text-lg font-bold text-slate-800">{value}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  )
}
